import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Plus, Trash, PencilSimple, ArrowClockwise, CheckCircle, Clock, Prohibit, CircleDashed, FloppyDisk, Sparkle, EnvelopeSimple, ArrowRight } from "@phosphor-icons/react";

const STATUSES = [
  { key: "open", label: "Open", icon: CircleDashed, color: "#71717A" },
  { key: "in_progress", label: "In progress", icon: Clock, color: "#3B82F6" },
  { key: "blocked", label: "Blocked", icon: Prohibit, color: "#F59E0B" },
  { key: "done", label: "Done", icon: CheckCircle, color: "#10B981" },
];
const PRIORITIES = ["low", "medium", "high"];

const BLANK = { title: "", owner: "", due_date: "", priority: "medium", status: "open", meeting: "", notes: "" };

export default function ActionPortalMiniApp() {
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [filterStatus, setFilterStatus] = useState("all");
  const [saveOpen, setSaveOpen] = useState(false);
  const [savedLeadId, setSavedLeadId] = useState(null);
  const [saveForm, setSaveForm] = useState({ name: "", email: "", company: "", notes: "", consent_privacy: false, consent_marketing: false });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/demos/action-portal/actions");
      setActions(data);
    } catch (e) {
      toast.error("Could not load actions");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!editing.title?.trim()) { toast.error("Title required"); return; }
    try {
      if (editing.id) {
        await api.patch(`/demos/action-portal/actions/${editing.id}`, editing);
      } else {
        await api.post("/demos/action-portal/actions", editing);
      }
      toast.success("Saved");
      setEditing(null);
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const updateStatus = async (a, status) => {
    try {
      await api.patch(`/demos/action-portal/actions/${a.id}`, { status });
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this action?")) return;
    try {
      await api.delete(`/demos/action-portal/actions/${id}`);
      toast.success("Deleted");
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const reset = async () => {
    if (!window.confirm("Reset the demo back to sample actions?")) return;
    try {
      await api.post("/demos/action-portal/reset");
      toast.success("Demo reset");
      await load();
    } catch (e) { toast.error("Reset failed"); }
  };

  const saveBoard = async () => {
    if (!saveForm.name.trim() || !saveForm.email.trim()) {
      toast.error("Name and email required");
      return;
    }
    if (!saveForm.consent_privacy) {
      toast.error("Please tick the consent box to continue");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/demos/action-portal/save-board", saveForm);
      setSavedLeadId(data.lead_id);
      toast.success("Saved — we'll be in touch.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not save");
    } finally { setSaving(false); }
  };

  const filtered = filterStatus === "all" ? actions : actions.filter((a) => a.status === filterStatus);
  const counts = STATUSES.reduce((acc, s) => ({ ...acc, [s.key]: actions.filter((a) => a.status === s.key).length }), {});

  return (
    <div className="bg-white border border-zinc-200" data-testid="action-portal-mini-app">
      <div className="px-5 md:px-6 py-4 border-b border-zinc-200 flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 bg-gradient-to-br from-blue-500 to-emerald-500 flex items-center justify-center">
            <span className="text-white font-black text-xs tracking-tighter">HL</span>
          </div>
          <div>
            <div className="font-bold text-sm" style={{ fontFamily: "'Outfit', sans-serif" }}>HLRA Action Portal</div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Demo · live CRUD</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={reset} className="px-3 py-1.5 border border-zinc-200 text-xs font-medium flex items-center gap-1.5 hover:bg-zinc-50" data-testid="action-reset">
            <ArrowClockwise size={14} /> Reset demo
          </button>
          <button onClick={() => setEditing({ ...BLANK })} className="btn-ghost-ink !py-1.5 !px-3 text-xs" data-testid="action-new">
            <Plus size={14} weight="bold" /> New action
          </button>
          <button onClick={() => setSaveOpen(true)} className="btn-signal !py-1.5 !px-3 text-xs" data-testid="action-save-board-header">
            <FloppyDisk size={14} weight="fill" /> Save my board
          </button>
        </div>
      </div>

      {/* Filter chips */}
      <div className="px-5 md:px-6 py-3 border-b border-zinc-200 flex gap-2 overflow-x-auto no-scrollbar">
        <Chip active={filterStatus === "all"} onClick={() => setFilterStatus("all")} label={`All · ${actions.length}`} testid="action-filter-all" />
        {STATUSES.map((s) => (
          <Chip key={s.key} active={filterStatus === s.key} onClick={() => setFilterStatus(s.key)} label={`${s.label} · ${counts[s.key] || 0}`} color={s.color} testid={`action-filter-${s.key}`} />
        ))}
      </div>

      {/* Nudge banner — appears once the visitor shows real engagement */}
      {actions.length >= 5 && !savedLeadId && (
        <div className="px-5 md:px-6 py-3 border-b border-zinc-200 bg-[hsl(var(--appistry-signal))]/5 flex flex-wrap items-center justify-between gap-3" data-testid="action-save-nudge">
          <div className="flex items-start gap-2 text-sm">
            <Sparkle size={18} weight="fill" className="text-[hsl(var(--appistry-signal))] flex-shrink-0 mt-0.5" />
            <div>
              <b>This is looking like a real board.</b> Save it and we'll send you a private sign-up link for a version built around your team.
            </div>
          </div>
          <button onClick={() => setSaveOpen(true)} className="btn-signal !py-1.5 !px-3 text-xs" data-testid="action-save-nudge-cta">
            <EnvelopeSimple size={14} weight="bold" /> Save & email me
          </button>
        </div>
      )}
      {savedLeadId && !saveOpen && (
        <div className="px-5 md:px-6 py-3 border-b border-zinc-200 bg-emerald-50 flex items-center gap-2 text-sm text-emerald-900" data-testid="action-saved-banner">
          <CheckCircle size={18} weight="fill" className="text-emerald-500 flex-shrink-0" />
          <span>Saved. We'll be in touch within one working day.</span>
        </div>
      )}

      {/* Table */}
      <div className="overflow-x-auto">
        {loading ? (
          <div className="p-10 text-center font-mono text-sm text-zinc-500">Loading actions…</div>
        ) : filtered.length === 0 ? (
          <div className="p-10 text-center font-mono text-sm text-zinc-500">No actions in this view.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-zinc-50">
              <tr className="text-left">
                {["Status", "Action", "Owner", "Due", "Priority", ""].map((h) => (
                  <th key={h} className="px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-zinc-500">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((a) => {
                const s = STATUSES.find((x) => x.key === a.status) || STATUSES[0];
                const Icon = s.icon;
                return (
                  <tr key={a.id} className="border-t border-zinc-200 hover:bg-zinc-50 group" data-testid={`action-row-${a.id}`}>
                    <td className="px-4 py-3">
                      <select value={a.status} onChange={(e) => updateStatus(a, e.target.value)} className="border border-zinc-200 px-2 py-1 text-xs bg-white flex items-center" data-testid={`action-status-${a.id}`}>
                        {STATUSES.map((st) => <option key={st.key} value={st.key}>{st.label}</option>)}
                      </select>
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-zinc-900">{a.title}</div>
                      {a.meeting && <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mt-0.5">{a.meeting}</div>}
                      {a.notes && <div className="text-xs text-zinc-500 mt-1">{a.notes}</div>}
                    </td>
                    <td className="px-4 py-3 text-zinc-700">{a.owner || "—"}</td>
                    <td className="px-4 py-3 text-xs font-mono text-zinc-600">{a.due_date ? new Date(a.due_date).toLocaleDateString() : "—"}</td>
                    <td className="px-4 py-3">
                      <PriorityPill p={a.priority} />
                    </td>
                    <td className="px-4 py-3 text-right opacity-0 group-hover:opacity-100 transition-opacity">
                      <button onClick={() => setEditing(a)} className="text-zinc-500 hover:text-zinc-900 mr-2" data-testid={`action-edit-${a.id}`}><PencilSimple size={14} /></button>
                      <button onClick={() => remove(a.id)} className="text-zinc-500 hover:text-red-600" data-testid={`action-delete-${a.id}`}><Trash size={14} /></button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Modal */}
      {editing && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-end md:items-center justify-center p-4" onClick={() => setEditing(null)}>
          <div className="bg-white max-w-lg w-full max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()} data-testid="action-editor">
            <div className="px-5 py-4 border-b border-zinc-200 flex items-center justify-between">
              <h3 className="font-semibold">{editing.id ? "Edit action" : "New action"}</h3>
              <button onClick={() => setEditing(null)} className="text-zinc-500 text-xl leading-none">×</button>
            </div>
            <div className="p-5 space-y-3">
              <Input label="Title" value={editing.title} onChange={(v) => setEditing({ ...editing, title: v })} testid="action-title" />
              <div className="grid grid-cols-2 gap-3">
                <Input label="Owner" value={editing.owner} onChange={(v) => setEditing({ ...editing, owner: v })} testid="action-owner" />
                <label className="text-sm">
                  <span className="font-semibold text-zinc-800 mb-1 block">Due date</span>
                  <input type="date" value={editing.due_date?.slice(0, 10) || ""} onChange={(e) => setEditing({ ...editing, due_date: e.target.value })} className="w-full border border-zinc-200 px-3 py-2 bg-white" data-testid="action-due" />
                </label>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <label className="text-sm">
                  <span className="font-semibold text-zinc-800 mb-1 block">Priority</span>
                  <select value={editing.priority} onChange={(e) => setEditing({ ...editing, priority: e.target.value })} className="w-full border border-zinc-200 px-3 py-2 bg-white" data-testid="action-priority">
                    {PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
                  </select>
                </label>
                <label className="text-sm">
                  <span className="font-semibold text-zinc-800 mb-1 block">Status</span>
                  <select value={editing.status} onChange={(e) => setEditing({ ...editing, status: e.target.value })} className="w-full border border-zinc-200 px-3 py-2 bg-white">
                    {STATUSES.map((st) => <option key={st.key} value={st.key}>{st.label}</option>)}
                  </select>
                </label>
              </div>
              <Input label="Meeting" value={editing.meeting} onChange={(v) => setEditing({ ...editing, meeting: v })} placeholder="e.g. Apr Committee" />
              <label className="block text-sm">
                <span className="font-semibold text-zinc-800 mb-1 block">Notes</span>
                <textarea rows={3} value={editing.notes || ""} onChange={(e) => setEditing({ ...editing, notes: e.target.value })} className="w-full border border-zinc-200 px-3 py-2 bg-white" />
              </label>
            </div>
            <div className="px-5 py-3 border-t border-zinc-200 flex justify-end gap-2">
              <button onClick={() => setEditing(null)} className="btn-ghost-ink !py-2 !px-3 text-sm">Cancel</button>
              <button onClick={save} className="btn-signal !py-2 !px-3 text-sm" data-testid="action-save">Save</button>
            </div>
          </div>
        </div>
      )}

      {/* Save-my-board modal */}
      {saveOpen && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-end md:items-center justify-center p-4" onClick={() => setSaveOpen(false)}>
          <div className="bg-white max-w-lg w-full max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()} data-testid="save-board-modal">
            <div className="px-5 py-4 border-b border-zinc-200 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <FloppyDisk size={18} weight="fill" className="text-[hsl(var(--appistry-signal))]" />
                <h3 className="font-semibold">Save my board</h3>
              </div>
              <button onClick={() => setSaveOpen(false)} className="text-zinc-500 text-xl leading-none">×</button>
            </div>
            {savedLeadId ? (
              <div className="p-8 text-center" data-testid="save-board-success">
                <CheckCircle size={44} weight="fill" className="text-emerald-500 mx-auto mb-4" />
                <h4 className="text-xl font-bold tracking-tight mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>Got it — {actions.length} action{actions.length === 1 ? "" : "s"} saved.</h4>
                <p className="text-sm text-zinc-600 max-w-sm mx-auto mb-6">We've got your board and we'll reply within one working day with a private sign-up link tailored to your team.</p>
                <button onClick={() => setSaveOpen(false)} className="btn-ghost-ink !py-2 !px-4 text-sm">Back to the demo <ArrowRight size={14} weight="bold" /></button>
              </div>
            ) : (
              <>
                <div className="p-5 space-y-3">
                  <p className="text-sm text-zinc-600">We'll keep a snapshot of the {actions.length} action{actions.length === 1 ? "" : "s"} you just created and send you a private link to spin up your own version. No spam, ever.</p>
                  <Input label="Full name" value={saveForm.name} onChange={(v) => setSaveForm({ ...saveForm, name: v })} testid="save-board-name" />
                  <Input label="Work email" value={saveForm.email} onChange={(v) => setSaveForm({ ...saveForm, email: v })} testid="save-board-email" />
                  <Input label="Company / team (optional)" value={saveForm.company} onChange={(v) => setSaveForm({ ...saveForm, company: v })} testid="save-board-company" />
                  <label className="block text-sm">
                    <span className="font-semibold text-zinc-800 mb-1 block">Anything we should know? (optional)</span>
                    <textarea rows={2} value={saveForm.notes} onChange={(e) => setSaveForm({ ...saveForm, notes: e.target.value })} className="w-full border border-zinc-200 px-3 py-2 bg-white" data-testid="save-board-notes" />
                  </label>
                  <label className="flex items-start gap-2 text-xs text-zinc-700 cursor-pointer">
                    <input type="checkbox" checked={saveForm.consent_privacy} onChange={(e) => setSaveForm({ ...saveForm, consent_privacy: e.target.checked })} className="mt-0.5" data-testid="save-board-consent-privacy" />
                    I'm happy for Appistry to store these details and contact me about this.
                  </label>
                  <label className="flex items-start gap-2 text-xs text-zinc-700 cursor-pointer">
                    <input type="checkbox" checked={saveForm.consent_marketing} onChange={(e) => setSaveForm({ ...saveForm, consent_marketing: e.target.checked })} className="mt-0.5" data-testid="save-board-consent-marketing" />
                    Occasionally, send me practical ideas (no salesy stuff).
                  </label>
                </div>
                <div className="px-5 py-3 border-t border-zinc-200 flex justify-end gap-2">
                  <button onClick={() => setSaveOpen(false)} className="btn-ghost-ink !py-2 !px-3 text-sm">Cancel</button>
                  <button onClick={saveBoard} disabled={saving} className="btn-signal !py-2 !px-3 text-sm disabled:opacity-60" data-testid="save-board-submit">
                    {saving ? "Saving…" : "Save & email me"} <ArrowRight size={14} weight="bold" />
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Chip({ active, onClick, label, color, testid }) {
  return (
    <button onClick={onClick} data-testid={testid} className={`px-3 py-1.5 text-xs font-medium border whitespace-nowrap transition-colors ${active ? "bg-zinc-950 text-white border-zinc-950" : "bg-white text-zinc-700 border-zinc-200 hover:border-zinc-400"}`} style={active && color ? { borderColor: color, backgroundColor: color } : {}}>
      {label}
    </button>
  );
}

function PriorityPill({ p }) {
  const colors = { low: "#71717A", medium: "#3B82F6", high: "#E11D48" };
  return <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest" style={{ color: colors[p] || "#71717A" }}>
    <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: colors[p] || "#71717A" }} />
    {p}
  </span>;
}

function Input({ label, value, onChange, testid, placeholder }) {
  return (
    <label className="block text-sm">
      <span className="font-semibold text-zinc-800 mb-1 block">{label}</span>
      <input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="w-full border border-zinc-200 px-3 py-2 bg-white" data-testid={testid} />
    </label>
  );
}
